/**
 * AssetConfigPage.jsx
 *
 * Settings configuration page for Asset Management.
 * Provides management interfaces for asset categories and sub-categories.
 * Follows the Service Provider Contracts page look and feel with table headers
 * and content left aligned consistently.
 *
 * Features:
 *   - Two tabs: Categories and Sub-Categories
 *   - CRUD operations for both entity types
 *   - Inline editing for quick updates
 *   - Soft-delete (activate/deactivate) toggle
 *   - Sort order management
 *
 * @module SettingsPages/AssetConfigPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaPlus, FaEdit, FaSave, FaTimes, FaTrashAlt, FaUndo,
    FaSpinner, FaExclamationTriangle, FaChevronDown, FaChevronUp,
    FaLayerGroup, FaTag
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * AssetConfigPage Component
 *
 * Renders the asset management configuration interface with tabs for
 * managing categories and sub-categories.
 */
const AssetConfigPage = () => {
    const { apiFetch } = useAuth();

    // ── State ────────────────────────────────────────────────────────────────

    const [activeTab, setActiveTab] = useState('categories');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Category form state
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '', sortOrder: 0 });
    const [saving, setSaving] = useState(false);

    // Sub-category form state
    const [showSubCategoryForm, setShowSubCategoryForm] = useState(false);
    const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
    const [subCategoryForm, setSubCategoryForm] = useState({ assetCategoryId: '', name: '', description: '', sortOrder: 0 });

    // Expanded categories (for sub-category view)
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // ── Data Loading ─────────────────────────────────────────────────────────

    /**
     * Fetches all categories with their sub-categories from the API.
     */
    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiFetch('/api/AssetManagement/categories');
            const data = await response.json();
            if (data.success) {
                setCategories(data.data || []);
            } else {
                setError(data.message || 'Failed to load categories');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setError('Failed to load categories. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // ── Category Operations ──────────────────────────────────────────────────

    /**
     * Opens the category creation form with empty values.
     */
    const handleNewCategory = () => {
        setCategoryForm({ name: '', description: '', sortOrder: categories.length });
        setEditingCategoryId(null);
        setShowCategoryForm(true);
    };

    /**
     * Opens the category edit form with pre-filled values.
     * @param {Object} category - The category to edit
     */
    const handleEditCategory = (category) => {
        setCategoryForm({
            name: category.name,
            description: category.description || '',
            sortOrder: category.sortOrder
        });
        setEditingCategoryId(category.assetCategoryId);
        setShowCategoryForm(true);
    };

    /**
     * Saves the category (create or update).
     */
    const handleSaveCategory = async () => {
        if (!categoryForm.name.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            setSaving(true);
            const isUpdate = editingCategoryId !== null;
            const url = isUpdate
                ? `/api/AssetManagement/categories/${editingCategoryId}`
                : '/api/AssetManagement/categories';
            const method = isUpdate ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(categoryForm)
            });
            const data = await response.json();

            if (data.success) {
                toast.success(isUpdate ? 'Category updated' : 'Category created');
                setShowCategoryForm(false);
                setEditingCategoryId(null);
                fetchCategories();
            } else {
                toast.error(data.message || 'Failed to save category');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to save category. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggles the active status of a category.
     * @param {Object} category - The category to toggle
     */
    const handleToggleCategoryActive = async (category) => {
        const confirmMessage = category.isActive
            ? `Deactivate category "${category.name}"? Assets in this category will remain but the category won't be selectable for new assets.`
            : `Reactivate category "${category.name}"?`;
        if (!confirm(confirmMessage)) return;

        try {
            const response = await apiFetch(`/api/AssetManagement/categories/${category.assetCategoryId}/toggle-active`, {
                method: 'PATCH'
            });
            const data = await response.json();
            if (data.success) {
                toast.success(category.isActive ? 'Category deactivated' : 'Category reactivated');
                fetchCategories();
            } else {
                toast.error(data.message || 'Failed to update category');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to update category. Please try again.');
        }
    };

    // ── Sub-Category Operations ──────────────────────────────────────────────

    /**
     * Opens the sub-category creation form with empty values.
     * @param {number} categoryId - Optional pre-selected parent category
     */
    const handleNewSubCategory = (categoryId = '') => {
        setSubCategoryForm({ assetCategoryId: categoryId.toString(), name: '', description: '', sortOrder: 0 });
        setEditingSubCategoryId(null);
        setShowSubCategoryForm(true);
    };

    /**
     * Opens the sub-category edit form with pre-filled values.
     * @param {Object} subCategory - The sub-category to edit
     */
    const handleEditSubCategory = (subCategory) => {
        setSubCategoryForm({
            assetCategoryId: subCategory.assetCategoryId.toString(),
            name: subCategory.name,
            description: subCategory.description || '',
            sortOrder: subCategory.sortOrder
        });
        setEditingSubCategoryId(subCategory.assetSubCategoryId);
        setShowSubCategoryForm(true);
    };

    /**
     * Saves the sub-category (create or update).
     */
    const handleSaveSubCategory = async () => {
        if (!subCategoryForm.name.trim()) {
            toast.error('Sub-category name is required');
            return;
        }
        if (!subCategoryForm.assetCategoryId) {
            toast.error('Parent category is required');
            return;
        }

        try {
            setSaving(true);
            const isUpdate = editingSubCategoryId !== null;
            const url = isUpdate
                ? `/api/AssetManagement/subcategories/${editingSubCategoryId}`
                : '/api/AssetManagement/subcategories';
            const method = isUpdate ? 'PUT' : 'POST';

            const payload = {
                ...subCategoryForm,
                assetCategoryId: parseInt(subCategoryForm.assetCategoryId)
            };

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (data.success) {
                toast.success(isUpdate ? 'Sub-category updated' : 'Sub-category created');
                setShowSubCategoryForm(false);
                setEditingSubCategoryId(null);
                fetchCategories();
            } else {
                toast.error(data.message || 'Failed to save sub-category');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to save sub-category. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Toggles the active status of a sub-category.
     * @param {Object} subCategory - The sub-category to toggle
     */
    const handleToggleSubCategoryActive = async (subCategory) => {
        const confirmMessage = subCategory.isActive
            ? `Deactivate sub-category "${subCategory.name}"?`
            : `Reactivate sub-category "${subCategory.name}"?`;
        if (!confirm(confirmMessage)) return;

        try {
            const response = await apiFetch(`/api/AssetManagement/subcategories/${subCategory.assetSubCategoryId}/toggle-active`, {
                method: 'PATCH'
            });
            const data = await response.json();
            if (data.success) {
                toast.success(subCategory.isActive ? 'Sub-category deactivated' : 'Sub-category reactivated');
                fetchCategories();
            } else {
                toast.error(data.message || 'Failed to update sub-category');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to update sub-category. Please try again.');
        }
    };

    /**
     * Toggles the expanded state of a category row to show/hide its sub-categories.
     * @param {number} categoryId - The category ID to toggle
     */
    const toggleCategoryExpanded = (categoryId) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="asset-config-page">
            <h2>Asset Management Configuration</h2>
            <p className="page-description">
                Configure asset categories and sub-categories used throughout the Asset Management system.
                Categories provide top-level classification (e.g. Fire, HVAC, Electrical) while sub-categories
                offer more granular grouping within each category.
            </p>

            {/* Tab Navigation */}
            <div className="config-tabs">
                <button
                    className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
                    onClick={() => setActiveTab('categories')}
                >
                    <FaLayerGroup /> Categories
                </button>
                <button
                    className={`tab-btn ${activeTab === 'subcategories' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subcategories')}
                >
                    <FaTag /> Sub-Categories
                </button>
            </div>

            {/* Error State */}
            {error && (
                <div className="alert alert-danger">
                    <FaExclamationTriangle /> {error}
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="loading-container">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading configuration...</p>
                </div>
            )}

            {/* Categories Tab */}
            {!loading && activeTab === 'categories' && (
                <div className="config-section">
                    <div className="section-header">
                        <h3>Asset Categories</h3>
                        <button className="btn-create" onClick={handleNewCategory}>
                            <FaPlus /> New Category
                        </button>
                    </div>

                    {/* Category Form */}
                    {showCategoryForm && (
                        <div className="inline-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={categoryForm.name}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                        placeholder="e.g. Fire, HVAC, Electrical"
                                        autoFocus
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={categoryForm.description}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                        placeholder="Optional description"
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>Shown in asset lists and reports to clarify what belongs in this category.</small>
                                </div>
                                <div className="form-group form-group-narrow">
                                    <label>Sort Order</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={categoryForm.sortOrder}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: parseInt(e.target.value) || 0 })}
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>Lower numbers appear first in dropdowns and lists.</small>
                                </div>
                                <div className="form-actions">
                                    <button className="btn-primary" onClick={handleSaveCategory} disabled={saving}>
                                        {saving ? <FaSpinner className="fa-spin" /> : <FaSave />} {editingCategoryId ? 'Update' : 'Create'}
                                    </button>
                                    <button className="btn-secondary" onClick={() => setShowCategoryForm(false)} disabled={saving}>
                                        <FaTimes /> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Categories Table */}
                    <table className="config-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Name</th>
                                <th style={{ textAlign: 'left' }}>Description</th>
                                <th style={{ textAlign: 'left' }}>Sub-Categories</th>
                                <th style={{ textAlign: 'left' }}>Sort Order</th>
                                <th style={{ textAlign: 'left' }}>Status</th>
                                <th style={{ textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="empty-state">
                                        No categories configured yet. Click "New Category" to get started.
                                    </td>
                                </tr>
                            ) : (
                                categories.map(category => (
                                    <tr key={category.assetCategoryId} className={!category.isActive ? 'inactive-row' : ''}>
                                        <td style={{ textAlign: 'left' }}><strong>{category.name}</strong></td>
                                        <td style={{ textAlign: 'left' }}>{category.description || '-'}</td>
                                        <td style={{ textAlign: 'left' }}>{category.subCategories?.length || 0}</td>
                                        <td style={{ textAlign: 'left' }}>{category.sortOrder}</td>
                                        <td style={{ textAlign: 'left' }}>
                                            <span className={`status-badge ${category.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                                {category.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'left' }} className="action-buttons">
                                            <button className="btn-icon" onClick={() => handleEditCategory(category)} title="Edit">
                                                <FaEdit />
                                            </button>
                                            <button
                                                className={`btn-icon ${category.isActive ? 'btn-danger' : 'btn-success'}`}
                                                onClick={() => handleToggleCategoryActive(category)}
                                                title={category.isActive ? 'Deactivate' : 'Reactivate'}
                                            >
                                                {category.isActive ? <FaTrashAlt /> : <FaUndo />}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Sub-Categories Tab */}
            {!loading && activeTab === 'subcategories' && (
                <div className="config-section">
                    <div className="section-header">
                        <h3>Asset Sub-Categories</h3>
                        <button className="btn-create" onClick={() => handleNewSubCategory()}>
                            <FaPlus /> New Sub-Category
                        </button>
                    </div>

                    {/* Sub-Category Form */}
                    {showSubCategoryForm && (
                        <div className="inline-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Parent Category *</label>
                                    <select
                                        value={subCategoryForm.assetCategoryId}
                                        onChange={(e) => setSubCategoryForm({ ...subCategoryForm, assetCategoryId: e.target.value })}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.filter(c => c.isActive).map(c => (
                                            <option key={c.assetCategoryId} value={c.assetCategoryId}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={subCategoryForm.name}
                                        onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                                        placeholder="e.g. Escalators, Lifts, Service Lifts"
                                        autoFocus={!!subCategoryForm.assetCategoryId}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={subCategoryForm.description}
                                        onChange={(e) => setSubCategoryForm({ ...subCategoryForm, description: e.target.value })}
                                        placeholder="Optional description"
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>Describes the sub-category for asset classification and reporting.</small>
                                </div>
                                <div className="form-group form-group-narrow">
                                    <label>Sort Order</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={subCategoryForm.sortOrder}
                                        onChange={(e) => setSubCategoryForm({ ...subCategoryForm, sortOrder: parseInt(e.target.value) || 0 })}
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>Lower numbers appear first.</small>
                                </div>
                                <div className="form-actions">
                                    <button className="btn-primary" onClick={handleSaveSubCategory} disabled={saving}>
                                        {saving ? <FaSpinner className="fa-spin" /> : <FaSave />} {editingSubCategoryId ? 'Update' : 'Create'}
                                    </button>
                                    <button className="btn-secondary" onClick={() => setShowSubCategoryForm(false)} disabled={saving}>
                                        <FaTimes /> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Sub-Categories grouped by parent category */}
                    {categories.filter(c => c.isActive).map(category => (
                        <div key={category.assetCategoryId} className="subcategory-group">
                            <div
                                className="subcategory-group-header"
                                onClick={() => toggleCategoryExpanded(category.assetCategoryId)}
                            >
                                {expandedCategories.has(category.assetCategoryId) ? <FaChevronUp /> : <FaChevronDown />}
                                <strong>{category.name}</strong>
                                <span className="count-badge">{category.subCategories?.length || 0}</span>
                                <button
                                    className="btn-icon btn-add-sub"
                                    onClick={(e) => { e.stopPropagation(); handleNewSubCategory(category.assetCategoryId); }}
                                    title="Add sub-category"
                                >
                                    <FaPlus />
                                </button>
                            </div>
                            {expandedCategories.has(category.assetCategoryId) && (
                                <table className="config-table subcategory-table">
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left' }}>Name</th>
                                            <th style={{ textAlign: 'left' }}>Description</th>
                                            <th style={{ textAlign: 'left' }}>Sort Order</th>
                                            <th style={{ textAlign: 'left' }}>Status</th>
                                            <th style={{ textAlign: 'left' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(!category.subCategories || category.subCategories.length === 0) ? (
                                            <tr>
                                                <td colSpan="5" className="empty-state">
                                                    No sub-categories yet. Click + to add one.
                                                </td>
                                            </tr>
                                        ) : (
                                            category.subCategories.map(subCategory => (
                                                <tr key={subCategory.assetSubCategoryId} className={!subCategory.isActive ? 'inactive-row' : ''}>
                                                    <td style={{ textAlign: 'left' }}>{subCategory.name}</td>
                                                    <td style={{ textAlign: 'left' }}>{subCategory.description || '-'}</td>
                                                    <td style={{ textAlign: 'left' }}>{subCategory.sortOrder}</td>
                                                    <td style={{ textAlign: 'left' }}>
                                                        <span className={`status-badge ${subCategory.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                                            {subCategory.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'left' }} className="action-buttons">
                                                        <button className="btn-icon" onClick={() => handleEditSubCategory(subCategory)} title="Edit">
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className={`btn-icon ${subCategory.isActive ? 'btn-danger' : 'btn-success'}`}
                                                            onClick={() => handleToggleSubCategoryActive(subCategory)}
                                                            title={subCategory.isActive ? 'Deactivate' : 'Reactivate'}
                                                        >
                                                            {subCategory.isActive ? <FaTrashAlt /> : <FaUndo />}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssetConfigPage;
